import { Button, Grid, Typography, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  BCPArrowUpArrowDown,
  BCPCalendarDay,
  BCPFilter,
  BCPSquarePlus,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";
import EventCard from "../../../components/layout/EventCard";
import { isAppMode } from "../../../global/AppOverlay";
import OptionsMenu from "../../../components/layout/OptionsMenu";
import {
  clearFilterSx,
  filterAndSortEvents,
  getFilterSx,
  updateFilters,
} from "../../../components/utilities/SortFilterFunctions";
import ListResponsive from "../../../components/other/ListResponsive";
import InfoContainer from "../../../components/layout/InfoContainer";

export default function OrgDashEvents(props) {
  const { loading, events, authContext, deleteEvent } = props;
  const appMode = isAppMode();
  const theme = useTheme();

  const [filteredEvents, setFilteredEvents] = useState(null);

  const [filters, setFilters] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);

  // Filter and sort events
  useEffect(() => {
    if (events?.length && filters.length) {
      const tempEvents = filterAndSortEvents(events, filters);

      setFilteredEvents(tempEvents);
    } else if (!filters.length) {
      setFilteredEvents(null);
    }
    // eslint-disable-next-line
  }, [filters, events]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={8}>
        <Typography variant="h3" fontWeight="700">
          Managed Events
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <Button
          variant="contained"
          href="/organize/events"
          target={`${appMode ? "_blank" : "_self"}`}
          startIcon={<BCPCalendarDay />}
          fullWidth
        >
          View Managed Events
        </Button>
      </Grid>

      <Grid item xs={12}>
        {!loading && !events?.length ? (
          <InfoContainer
            title="No events found"
            body={
              "It looks like there's nothing here. Create a new event to get started!"
            }
            ActionButton={
              <Button
                variant="contained"
                href={"/organize/event"}
                startIcon={<BCPSquarePlus />}
              >
                Create Event
              </Button>
            }
            sx={{ mt: 1 }}
          />
        ) : (
          <ListResponsive
            name="Managed Events"
            originalList={
              loading
                ? null
                : filteredEvents
                  ? filteredEvents
                  : events?.length
                    ? events
                    : []
            }
            doPages
            initialRowsPerPage={3}
            itemsPerPage={[3]}
            centerNav
            // doSearch
            actionButton={[
              {
                label: "Sort",
                startIcon: <BCPArrowUpArrowDown />,
                onClick: (e) => {
                  setAnchorEl(e.currentTarget);
                },
                disabled: loading || !events?.length,
                sx: {
                  ...getFilterSx(
                    Boolean(
                      filters.length &&
                        filters.some((item) => item.includes("sort"))
                    ),
                    filters,
                    theme
                  ),

                  mr: 1,
                },
              },
              {
                label: "Filters",
                startIcon: <BCPFilter />,
                onClick: (e) => {
                  setAnchorEl2(e.currentTarget);
                },
                disabled: loading || !events?.length,
                sx: {
                  ...getFilterSx(
                    Boolean(
                      filters.length &&
                        filters.some((item) => item.includes("filter"))
                    ),
                    filters,
                    theme
                  ),
                  ml: 1,
                  mr: 1,
                },
              },
              {
                label: "Clear Filters",
                startIcon: <BCPXmark />,
                disabled: loading || !events?.length,
                sx: {
                  ...clearFilterSx(false, filters, theme),
                  ml: 1,
                },
                onClick: () => {
                  updateFilters(null, null, null, setFilters);
                  setFilteredEvents(null);
                  setAnchorEl(null);
                  setAnchorEl2(null);
                },
              },
            ]}
            buttonGroup={false}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid key={index} item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                  <EventCard
                    event={row}
                    key={row?.id || index}
                    showStatus
                    organize
                    authContext={authContext}
                    deleteEvent={deleteEvent}
                    preventImageFetch
                  />
                </Grid>
              ),
            }}
          />
        )}
      </Grid>

      {/* Sorting */}
      <OptionsMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        items={[
          {
            name: "sort-date-asc",
            label: "Start Date Ascending",
            selected: filters.includes("sort-date-asc"),
            onClick: () =>
              updateFilters("sort-date-asc", false, filters, setFilters),
          },
          {
            name: "sort-date-desc",
            label: "Start Date Descending",
            selected: filters.includes("sort-date-desc"),
            onClick: () =>
              updateFilters("sort-date-desc", false, filters, setFilters),
          },
          {
            name: "sort-name-asc",
            label: "Name Ascending",
            selected: filters.includes("sort-name-asc"),
            onClick: () =>
              updateFilters("sort-name-asc", false, filters, setFilters),
          },
          {
            name: "sort-name-desc",
            label: "Name Descending",
            selected: filters.includes("sort-name-desc"),
            onClick: () =>
              updateFilters("sort-name-desc", false, filters, setFilters),
          },
          {
            name: "sort-rounds-asc",
            label: "Rounds Ascending",
            selected: filters.includes("sort-rounds-asc"),
            onClick: () =>
              updateFilters("sort-rounds-asc", false, filters, setFilters),
          },
          {
            name: "sort-rounds-desc",
            label: "Rounds Descending",
            selected: filters.includes("sort-rounds-desc"),
            onClick: () =>
              updateFilters("sort-rounds-desc", false, filters, setFilters),
          },
        ]}
        name="events-sort-menu"
      />

      {/* Filters */}
      <OptionsMenu
        anchorEl={anchorEl2}
        setAnchorEl={setAnchorEl2}
        items={[
          {
            name: "filter-online",
            label: "Online Event",
            selected: filters.includes("filter-online"),
            onClick: () =>
              updateFilters(
                "filter-online",
                "filter-exclude-online",
                filters,
                setFilters
              ),
          },
          {
            name: "filter-exclude-online",
            label: "Exclude Online",
            selected: filters.includes("filter-exclude-online"),
            onClick: () =>
              updateFilters(
                "filter-exclude-online",
                "filter-online",
                filters,
                setFilters
              ),
          },
          { divider: true },

          {
            name: "filter-singles",
            label: "Singles Events",
            selected: filters.includes("filter-singles"),
            onClick: () =>
              updateFilters(
                "filter-singles",
                ["filter-doubles", "filter-teams", "filter-board"],
                filters,
                setFilters
              ),
          },
          {
            name: "filter-doubles",
            label: "Doubles Events",
            selected: filters.includes("filter-doubles"),
            onClick: () =>
              updateFilters(
                "filter-doubles",
                ["filter-singles", "filter-teams", "filter-board"],
                filters,
                setFilters
              ),
          },
          {
            name: "filter-teams",
            label: "Teams Events",
            selected: filters.includes("filter-teams"),
            onClick: () =>
              updateFilters(
                "filter-teams",
                ["filter-singles", "filter-doubles", "filter-board"],
                filters,
                setFilters
              ),
          },
        ]}
        name="events-filter-menu"
      />
    </Grid>
  );
}
